import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import ProofWindow from "@/components/ProofWindow";
import AppLogo from "@/components/AppLogo";
import EyePreviewTile from "@/components/EyePreviewTile";

/**
 * ProofCanvas — host that listens on the BroadcastChannel for `proof_open`
 * messages from the main console. Each `proof_open` pushes a new draggable
 * card onto the canvas; user can close any card individually. As R.A.W.
 * narrates the brief (or any action), cards fade in showing the underlying
 * proof for each claim being spoken.
 *
 * Channel: "rawco-stage". Messages:
 *   { type: "proof_open", card: { id, kind, title, accent, x, y, data } }
 *   { type: "proof_close", id }
 *   { type: "proof_clear" }
 */
/**
 * @param {object} props
 * @param {"main"|"mirror"} [props.mode]
 *   "mirror" → listen on BroadcastChannel only (used inside /display window).
 *   "main"   → listen on local CustomEvents only (used on the orb screen).
 *   Without this split BOTH windows render every card → orb-screen pileup.
 */
export default function ProofCanvas({ mode = "main" }) {
  const [cards, setCards] = useState([]);
  const [topZ, setTopZ] = useState(20);

  // ─── PER-CARD POSITION PERSISTENCE ──────────────────────────────
  // Owner asked: "wherever I move anything it stays there no matter what".
  // We save each card's last x/y under its id so the next time the same
  // brief / dashboard fires, every tile lands exactly where it was left.
  // Storage is namespaced by `mode` so the orb screen and mirror screen
  // each remember their own layout independently.
  const POS_STORE_KEY = `rawco-proof-positions-${mode}`;
  const savedPositionsRef = useRef({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(POS_STORE_KEY);
      if (raw) savedPositionsRef.current = JSON.parse(raw) || {};
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 🔄 Reload positions when LayoutTemplatesModal applies a saved layout.
  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem(POS_STORE_KEY);
        if (raw) savedPositionsRef.current = JSON.parse(raw) || {};
        // Force re-flow: reset every card's x/y to the saved positions
        setCards(prev => prev.map(c => {
          const saved = savedPositionsRef.current[c.id];
          if (saved) return { ...c, x: saved.x, y: saved.y };
          return c;
        }));
      } catch { /* ignore */ }
    };
    window.addEventListener("rawco-positions-reload", handler);
    return () => window.removeEventListener("rawco-positions-reload", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCardPosition = useCallback((id, x, y) => {
    if (!id) return;
    savedPositionsRef.current[id] = { x, y };
    try {
      localStorage.setItem(POS_STORE_KEY, JSON.stringify(savedPositionsRef.current));
    } catch { /* ignore quota */ }
    // Also reflect in the cards array so future re-renders use the new pos
    setCards((prev) => prev.map((c) => c.id === id ? { ...c, x, y } : c));
  }, [POS_STORE_KEY]);

  const addCard = useCallback((card) => {
    setCards((prev) => {
      // If we have a saved position for this id, use it — owner explicitly
      // asked: "wherever I move anything it stays there no matter what".
      const saved = savedPositionsRef.current[card.id];
      if (saved) {
        card = { ...card, pinX: saved.x, pinY: saved.y };
      }
      // Dedupe by id — if exists, refresh data + bring to front
      const existing = prev.find((c) => c.id === card.id);
      if (existing) {
        return prev.map((c) => (c.id === card.id ? { ...c, ...card, zIndex: topZ + 1 } : c));
      }
      // ─── SPOTLIGHT MODE ────────────────────────────────────────
      // Sequential reveal during the morning brief — one card centre
      // stage at a time, big enough to read across the room. Cleared
      // before the next one arrives so there's never any overlap.
      if (card.spotlight) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const SPOTLIGHT_W = Math.min(540, vw - 80);
        const SPOTLIGHT_H = 280;
        return [{
          ...card,
          width: SPOTLIGHT_W,
          x: Math.max(20, (vw - SPOTLIGHT_W) / 2),
          y: Math.max(40, (vh - SPOTLIGHT_H) / 2 - 40),
          zIndex: topZ + 1,
        }];
      }
      // ─── GRID LAYOUT WITH COLLISION CHECK ───────────────────────
      // Owner repeatedly asked: "no card should overlap any other".
      // We build a list of all empty grid slots in the safe centre band,
      // then drop the new card in the FIRST slot that doesn't overlap an
      // existing card. If every slot is full, we wrap to a new row.
      // Mirror windows have no orb so we can use the FULL screen; orb
      // screen needs huge side margins to keep cards off the orb.
      // Owner asked: "spread them on all four rows from left to middle to
      // right" — so we size mirror cards smaller (280×170, gap 14) to fit
      // a proper 4×4 = 16-slot grid on a 1280×800 mirror.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMirror = mode === "mirror";
      const SAFE_LEFT  = isMirror ? 20 : 360;
      const SAFE_RIGHT = isMirror ? 20 : 360;
      const SAFE_TOP   = isMirror ? 20 : 90;
      const SAFE_BOT   = isMirror ? 20 : 120;
      const cardW = card.width || (isMirror ? 280 : 340);
      const cardH = isMirror ? 170 : 240;
      const GAP   = isMirror ? 14 : 24;
      const usableW = Math.max(cardW + GAP, vw - SAFE_LEFT - SAFE_RIGHT);
      const usableH = Math.max(cardH + GAP, vh - SAFE_TOP - SAFE_BOT);
      const cols = Math.max(1, Math.floor(usableW / (cardW + GAP)));
      const rows = Math.max(1, Math.floor(usableH / (cardH + GAP)));
      // Build all grid slots
      const slots = [];
      const colWidth = usableW / cols;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          slots.push({
            x: SAFE_LEFT + c * colWidth + (colWidth - cardW) / 2,
            y: SAFE_TOP + r * (cardH + GAP),
          });
        }
      }
      // Helper: does (x,y,w,h) overlap any existing card rectangle?
      const overlaps = (x, y) => prev.some((p) => {
        const pw = p.width || (isMirror ? 280 : 340);
        const ph = isMirror ? 170 : 240;
        return !(x + cardW < p.x || x > p.x + pw ||
                 y + cardH < p.y || y > p.y + ph);
      });
      // Find first free slot
      let chosen = slots.find(s => !overlaps(s.x, s.y));
      // No free slot → all 16 are full. Recycle through slots with a small
      // diagonal offset so the new card sits ON TOP of an earlier one
      // rather than disappearing off the bottom-right. The offset cycles
      // every (slots.length) so a 17th card lands at slot[0]+18px, 18th
      // at slot[1]+18px, etc — visually scannable, no off-screen cards.
      if (!chosen && slots.length) {
        const overflow = prev.length - slots.length;
        const offset = (Math.floor(overflow / slots.length) + 1) * 18;
        const base = slots[overflow % slots.length];
        chosen = {
          x: Math.min(base.x + offset, vw - SAFE_RIGHT - cardW),
          y: Math.min(base.y + offset, vh - SAFE_BOT  - cardH),
        };
      }
      if (!chosen) {
        chosen = { x: SAFE_LEFT, y: SAFE_TOP };
      }
      const finalX = card.pinX ?? chosen.x;
      const finalY = card.pinY ?? chosen.y;
      // Honour any saved "pinned" state — a card the owner pinned during
      // a previous session re-spawns already pinned, with no TTL.
      const wasPinned = pinnedSetRef.current.has(card.id);
      return [...prev, { ...card, x: finalX, y: finalY, zIndex: topZ + 1, spawnedAt: Date.now(), pinned: wasPinned || !!card.pinned }];
    });
    setTopZ((z) => z + 1);
  }, [topZ, mode]);

  const closeCard = useCallback((id) => {
    // ❌ closes AND unpins — owner wants the cross to be the only way to
    // permanently remove a pinned card. We drop it from the persisted set
    // so the next brief doesn't bring back a phantom pinned card.
    if (id && pinnedSetRef.current.has(id)) {
      pinnedSetRef.current.delete(id);
      try { localStorage.setItem(`rawco-proof-pinned-${mode}`, JSON.stringify([...pinnedSetRef.current])); } catch { /* ignore */ }
    }
    setCards((prev) => prev.filter((c) => c.id !== id));
  }, [mode]);

  // ─── PERSISTED "PINNED" STATE ─────────────────────────────────────
  // Owner asked: cards I CLICK should stay forever (until I press ❌);
  // cards I don't touch should still auto-vanish after 2 min. We persist
  // the pinned card-id set per mode so the pin survives reloads.
  const PIN_STORE_KEY = `rawco-proof-pinned-${mode}`;
  const pinnedSetRef = useRef(new Set());
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PIN_STORE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) pinnedSetRef.current = new Set(arr);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const savePinned = useCallback(() => {
    try {
      localStorage.setItem(PIN_STORE_KEY, JSON.stringify([...pinnedSetRef.current]));
    } catch { /* ignore quota */ }
  }, [PIN_STORE_KEY]);
  const togglePin = useCallback((id, pin = true) => {
    if (!id) return;
    if (pin) pinnedSetRef.current.add(id);
    else pinnedSetRef.current.delete(id);
    savePinned();
    setCards((prev) => prev.map((c) => c.id === id ? { ...c, pinned: pin } : c));
  }, [savePinned]);

  const focusCard = useCallback((id) => {
    setTopZ((z) => {
      const next = z + 1;
      // Clicking ANY card pins it — that's the contract. ❌ unpins/closes.
      pinnedSetRef.current.add(id);
      savePinned();
      setCards((prev) => prev.map((c) => (c.id === id ? { ...c, zIndex: next, pinned: true } : c)));
      return next;
    });
  }, [savePinned]);

  // Auto-fade cards after 6 minutes so the mirror doesn't keep stale
  // results forever. Each card gets its own timer; user can drag/click
  // to reset its TTL (focusCard already bumps zIndex; we extend life too).
  // FIX: use a ref-tracked Map keyed by card id so adding card #17 doesn't
  // tear down + rebuild timers for cards #1..#16 (was thrashing during the
  // 16-card morning brief fan-out and locking the main thread).
  const CARD_TTL_MS = 2 * 60 * 1000;  // Owner: "cards don't disappear after 2 min" — was 6min
  const cardTimersRef = useRef(new Map());
  useEffect(() => {
    const liveIds = new Set(cards.map((c) => c.id));
    // Cancel timers for cards that have been removed
    cardTimersRef.current.forEach((t, id) => {
      if (!liveIds.has(id)) {
        clearTimeout(t);
        cardTimersRef.current.delete(id);
      }
    });
    // Schedule timers only for NEW cards we haven't scheduled yet — AND
    // skip pinned cards entirely. Owner: "if I click a card it should
    // stay on screen 24/7 until I press the ❌". Pinning happens on click
    // (focusCard) or via the saved set (re-renders after a reload).
    const now = Date.now();
    cards.forEach((c) => {
      if (cardTimersRef.current.has(c.id)) return;
      if (c.pinned || pinnedSetRef.current.has(c.id)) return;  // pinned = no TTL
      const age = now - (c.spawnedAt || now);
      const remaining = Math.max(2000, CARD_TTL_MS - age);
      const t = setTimeout(() => {
        // Last-minute safety: if the owner pinned it during the timer's
        // life, honour that and skip the dismissal.
        if (pinnedSetRef.current.has(c.id)) {
          cardTimersRef.current.delete(c.id);
          return;
        }
        setCards((prev) => prev.map((p) => p.id === c.id ? { ...p, dying: true } : p));
        setTimeout(() => closeCard(c.id), 600);
        cardTimersRef.current.delete(c.id);
      }, remaining);
      cardTimersRef.current.set(c.id, t);
    });
  }, [cards, closeCard]);
  // Clear all timers on unmount
  useEffect(() => () => {
    cardTimersRef.current.forEach((t) => clearTimeout(t));
    cardTimersRef.current.clear();
  }, []);

  useEffect(() => {
    let bc = null;
    if (mode === "mirror") {
      // Mirror window: receive cards pushed from the orb screen.
      bc = new BroadcastChannel("rawco-stage");
      bc.onmessage = (e) => {
        const m = e.data || {};
        if (m.type === "proof_open" && m.card) addCard(m.card);
        else if (m.type === "proof_close" && m.id) closeCard(m.id);
        else if (m.type === "proof_clear") setCards([]);
      };
    }
    // Local dispatch fires ONLY on the orb screen when no mirror is open —
    // see App.js fanOutProofCards. Mirror ignores these.
    const onLocal = (e) => { if (mode === "main") addCard(e.detail); };
    const onClear = () => { if (mode === "main") setCards([]); };
    window.addEventListener("rawco-proof-open", onLocal);
    window.addEventListener("rawco-proof-clear", onClear);
    return () => {
      if (bc) bc.close();
      window.removeEventListener("rawco-proof-open", onLocal);
      window.removeEventListener("rawco-proof-clear", onClear);
    };
  }, [addCard, closeCard, mode]);

  return (
    <div className="proof-canvas-3d" data-testid="proof-canvas">
      {cards.map((c, idx) => {
        const depth = cards.length - 1 - idx;
        return (
          <div
            key={c.id}
            style={{
              opacity: c.dying ? 0 : 1,
              transition: "opacity 600ms ease",
            }}
          >
            <ProofWindow
              id={c.id}
              title={c.title}
              accent={c.accent}
              x={c.x}
              y={c.y}
              width={c.width || 360}
              zIndex={c.zIndex}
              depth={depth}
              onClose={closeCard}
              onFocus={focusCard}
              onMoved={saveCardPosition}
            >
              <ProofCardBody kind={c.kind} data={c.data || {}} />
            </ProofWindow>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Body renderers per card kind ────────────────────────────────── */
/**
 * useLiveCapital — subscribes to /jarvis/capital_pnl on a 5-second tick.
 * Returns the latest payload. Multiple components can share a single tick
 * via window cache so 4 open cards = 1 network request, not 4.
 */
function useLiveCapital() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      // Shared cache across components: only ONE network call per 5s tick
      // no matter how many proof cards are open.
      const now = Date.now();
      const cache = window.__rc_capital_cache;
      if (cache && (now - cache.ts) < 4500) {
        if (alive) setData(cache.data);
        return;
      }
      try {
        const r = await api.get("/jarvis/capital_pnl");
        window.__rc_capital_cache = { ts: now, data: r.data };
        if (alive) setData(r.data);
      } catch {}
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return data;
}

function ProofCardBody({ kind, data }) {
  // Hooks must be called at the top level — pull the live ticker once
  // and let each card pick the fields it cares about.
  const live = useLiveCapital();
  if (kind === "balance") {
    // Live overrides static snapshot — keeps the running balance ticking
    // forever after the brief has finished narrating.
    // Capital.com returns the account with a NESTED balance object:
    //   { accountId, accountName, currency, balance: { balance, available, deposit, profitLoss } }
    // Older snapshots / fallbacks may have a flat scalar `balance`. Unwrap both.
    const acct = live?.account || {};
    const bal = (acct.balance && typeof acct.balance === "object") ? acct.balance : null;
    const balance = bal?.balance ?? (typeof acct.balance === "number" ? acct.balance : data.balance);
    const available = bal?.available ?? acct.available ?? data.available;
    const upl = live?.open_upl_gbp ?? data.upl;
    const mode = live?.trade_mode ?? data.mode;
    const accountId = acct.accountId ?? acct.id ?? data.account_id;
    return (
      <>
        <div className="proof-big-num">£{fmt(balance)}</div>
        <div className="proof-sub">Account balance · {accountId || "—"} · <span style={{ color: "var(--rc-gold)", fontSize: 9 }}>LIVE</span></div>
        <div className="mt-3">
          <div className="proof-row"><span className="label">Available</span><span className="value">£{fmt(available)}</span></div>
          <div className="proof-row"><span className="label">P&L (open)</span><span className="value" style={{ color: Number(upl) >= 0 ? "#7CFFB7" : "#FF7A7A" }}>£{fmt(upl)}</span></div>
          <div className="proof-row"><span className="label">Mode</span><span className="value">{(mode || "—").toUpperCase()}</span></div>
        </div>
      </>
    );
  }
  if (kind === "positions") {
    // Live positions array — re-renders every 5s with fresh UPL numbers
    const livePos = live?.open_positions || [];
    const list = livePos.length ? livePos.map((p) => ({
      symbol: (p.market?.instrumentName) || (p.market?.epic) || "—",
      direction: p.position?.direction,
      upl: p.position?.upl ?? 0,
    })) : (data.positions || []);
    if (!list.length) return <div className="proof-sub">No open positions.</div>;
    return (
      <>
        <div className="proof-big-num">{list.length}</div>
        <div className="proof-sub">Open position{list.length === 1 ? "" : "s"} · <span style={{ color: "var(--rc-gold)", fontSize: 9 }}>LIVE</span></div>
        <div className="mt-3">
          {list.slice(0, 6).map((p, i) => (
            <div key={i} className="proof-row">
              <span className="label">{p.symbol} · {p.direction || ""}</span>
              <span className="value" style={{ color: Number(p.upl) >= 0 ? "#7CFFB7" : "#FF7A7A" }}>
                £{fmt(p.upl)}
              </span>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (kind === "messages") {
    const items = data.items || [];
    return (
      <>
        <div className="proof-big-num">{data.unread ?? items.length}</div>
        <div className="proof-sub">{data.unread ? "Unread messages" : "Recent messages"}</div>
        <div className="mt-3">
          {items.slice(0, 5).map((m, i) => (
            <div key={i} className="proof-row">
              <span className="label" style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.sender || m.from || "?"}
              </span>
              <span className="value" style={{ opacity: 0.7, fontSize: 10 }}>
                {(m.text || m.preview || "").slice(0, 40)}
              </span>
            </div>
          ))}
          {items.length === 0 && <div className="proof-sub">Inbox empty.</div>}
        </div>
      </>
    );
  }
  // ─── HUB DASHBOARD TILE ──────────────────────────────────────────
  // Mirrors a single tile from rawcompany.uk/admin (STRIPE IN, REFUNDS,
  // AVAILABLE, DONATIONS, APPS, COSTS/MO, THIS MONTH P&L, ALL-TIME
  // PROFIT, AFFILIATE CLICKS). One big neon number + label + optional
  // sub-line. No drill-down — these are read-out cards for the brief.
  if (kind === "hub_tile") {
    return (
      <>
        <div className="proof-big-num" style={{ fontSize: 42 }}>
          {data.value}
        </div>
        {data.sub && <div className="proof-sub">{data.sub}</div>}
      </>
    );
  }
  if (kind === "finance") {
    return (
      <>
        <div className="proof-big-num" style={{ color: Number(data.net) >= 0 ? "#7CFFB7" : "#FF7A7A" }}>
          £{fmt(data.net)}
        </div>
        <div className="proof-sub">Net last 24h · {data.entries || 0} entries</div>
        <div className="mt-3">
          <div className="proof-row"><span className="label">Income</span><span className="value">£{fmt(data.income)}</span></div>
          <div className="proof-row"><span className="label">Outgoing</span><span className="value">£{fmt(data.outgoing)}</span></div>
        </div>
      </>
    );
  }
  if (kind === "reminders") {
    const items = data.items || [];
    return (
      <>
        <div className="proof-big-num">{items.length}</div>
        <div className="proof-sub">Reminders today</div>
        <div className="mt-3">
          {items.slice(0, 6).map((r, i) => (
            <div key={i} className="proof-row">
              <span className="label" style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.text}</span>
              <span className="value" style={{ opacity: 0.6, fontSize: 10 }}>{(r.trigger_at || "").slice(11, 16)}</span>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (kind === "issues") {
    return (
      <>
        <div className="proof-big-num">{(data.needs_owner || 0) + (data.auto_open || 0)}</div>
        <div className="proof-sub">Open issues</div>
        <div className="mt-3">
          <div className="proof-row"><span className="label">Need owner</span><span className="value">{data.needs_owner || 0}</span></div>
          <div className="proof-row"><span className="label">Auto-handling</span><span className="value">{data.auto_open || 0}</span></div>
        </div>
      </>
    );
  }
  if (kind === "spend") {
    return (
      <>
        <div className="proof-big-num">£{fmt(data.spend_gbp)}</div>
        <div className="proof-sub">Emergent spend · {data.transactions || ""}</div>
      </>
    );
  }
  if (kind === "hub") {
    // RAW Hub aggregate — total across all sister apps + per-app breakdown
    const apps = data.apps || [];
    return (
      <>
        <div className="proof-big-num">£{fmt(data.total)}</div>
        <div className="proof-sub">Raw Hub · {apps.length} app{apps.length === 1 ? "" : "s"} · {data.users || 0} users</div>
        <div className="mt-3">
          {apps.slice(0, 5).map((a, i) => (
            <div key={i} className="proof-row">
              <span className="label">{a.name || a.key}</span>
              <span className="value">£{fmt(a.revenue_gbp)} · {a.users || 0}u</span>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (kind === "news") {
    const items = data.items || [];
    if (!items.length) return <div className="proof-sub">No headlines yet.</div>;
    return (
      <>
        <div className="proof-sub" style={{ fontSize: 11, opacity: 0.7, marginBottom: 8 }}>BBC News · latest</div>
        <div>
          {items.map((n, i) => (
            <div key={i} style={{ paddingBottom: 8, marginBottom: 8, borderBottom: i < items.length - 1 ? "1px solid rgba(212,175,55,0.15)" : "none" }}>
              <div style={{ fontSize: 12, color: "var(--rc-gold)", lineHeight: 1.4, fontWeight: 600 }}>{n.title}</div>
              {n.summary && <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4, lineHeight: 1.4 }}>{n.summary.slice(0, 110)}{n.summary.length > 110 ? "…" : ""}</div>}
            </div>
          ))}
        </div>
      </>
    );
  }
  if (kind === "imagegallery") {
    // Owner asked: "can I ask you to show me anything off the Internet —
    // show me a diagram of a Porsche?" — renders the images returned by
    // Tavily image search in a 2-column grid. Click an image to open the
    // full-size source in a new tab.
    const imgs = Array.isArray(data.images) ? data.images.slice(0, 6) : [];
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {imgs.map((img, i) => (
          <a
            key={i}
            href={img.url}
            target="_blank"
            rel="noopener noreferrer"
            title={img.description || data.query || "Open full size"}
            style={{
              display: "block",
              aspectRatio: "4/3",
              border: "1px solid var(--rc-gold-dim)",
              overflow: "hidden",
              background: "#0a0e18",
              cursor: "pointer",
              transition: "border-color 0.2s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--rc-gold)"; e.currentTarget.style.transform = "scale(1.02)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--rc-gold-dim)"; e.currentTarget.style.transform = "scale(1)"; }}
          >
            <img
              src={img.url}
              alt={img.description || `${data.query} ${i + 1}`}
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </a>
        ))}
        {imgs.length === 0 && (
          <div style={{ gridColumn: "span 2", textAlign: "center", padding: 12, fontSize: 10, opacity: 0.6 }}>
            No images found for "{data.query}"
          </div>
        )}
      </div>
    );
  }
  if (kind === "eye_preview") {
    // 👁 LIVE EYE preview — polls /api/jarvis/camera/latest every 2s and
    // renders the most recent ESP32-S3 frame as a continuously-refreshing
    // image. Owner asked: "will it show a live video on my app of what is
    // looking at, yeah?" — this is the live feed.
    return <EyePreviewTile />;
  }
  if (kind === "weather") {
    // condition text so the owner can glance and know if they need a coat.
    const code = data.code ?? 0;
    const condition =
      code === 0 ? "Clear" :
      code <= 3 ? "Partly cloudy" :
      code <= 48 ? "Foggy" :
      code <= 67 ? "Drizzle" :
      code <= 77 ? "Snow" :
      code <= 82 ? "Rain showers" :
      code <= 99 ? "Thunder" : "—";
    const glyph =
      code === 0 ? "☀" :
      code <= 3 ? "⛅" :
      code <= 48 ? "🌫" :
      code <= 67 ? "🌦" :
      code <= 77 ? "❄" :
      code <= 82 ? "🌧" :
      code <= 99 ? "⛈" : "•";
    return (
      <>
        <div style={{ fontSize: 56, lineHeight: 1, textAlign: "center", color: "var(--rc-cyan, #6dd5ff)", textShadow: "0 0 18px rgba(109,213,255,0.55)" }}>{glyph}</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 38, textAlign: "center", color: "var(--rc-text)", marginTop: 6 }}>
          {data.temp_c != null ? `${Math.round(data.temp_c)}°` : "—"}
        </div>
        <div style={{ fontSize: 11, textAlign: "center", opacity: 0.7, marginTop: 4 }}>{condition}</div>
        {data.wind != null && (
          <div style={{ fontSize: 10, textAlign: "center", opacity: 0.5, marginTop: 4 }}>Wind {Math.round(data.wind)} km/h</div>
        )}
      </>
    );
  }
  if (kind === "calendar") {
    // 🗓 CALENDAR tile — owner asked: "show actual full month calendar
    // from 1 to 31". We render a proper month grid with today highlighted
    // in gold, dots on days that have reminders, and a list of today's
    // events below.
    const items = Array.isArray(data.items) ? data.items : [];
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7; // Monday=0
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    // Build a set of days that have reminders (today's items use their day)
    const remDays = new Set();
    items.forEach((r) => {
      try {
        const dt = new Date(r.trigger_at);
        if (dt.getMonth() === month && dt.getFullYear() === year) remDays.add(dt.getDate());
      } catch { /* ignore */ }
    });
    const monthName = today.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const dayHeaders = ["M", "T", "W", "T", "F", "S", "S"];
    return (
      <>
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
                       color: "var(--rc-gold)", marginBottom: 8, textAlign: "center" }}>
          {monthName}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 8 }}>
          {dayHeaders.map((h, i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 9, opacity: 0.5,
                                  fontFamily: "monospace", padding: "2px 0" }}>{h}</div>
          ))}
          {cells.map((d, i) => {
            const isToday = d === today.getDate();
            const hasRem = d && remDays.has(d);
            return (
              <div key={i} style={{
                textAlign: "center", fontSize: 10, padding: "4px 0",
                fontFamily: "'JetBrains Mono', monospace",
                color: isToday ? "#000" : "var(--rc-text)",
                background: isToday ? "var(--rc-gold)" : "transparent",
                borderRadius: 3, position: "relative", minHeight: 18,
                opacity: d ? 1 : 0,
                fontWeight: isToday ? 700 : 400,
              }}>
                {d || ""}
                {hasRem && !isToday && (
                  <div style={{
                    position: "absolute", bottom: 1, left: "50%",
                    transform: "translateX(-50%)",
                    width: 3, height: 3, borderRadius: "50%",
                    background: "var(--rc-gold)",
                  }} />
                )}
              </div>
            );
          })}
        </div>
        {items.length === 0 ? (
          <div style={{ fontSize: 10, opacity: 0.5, textAlign: "center", padding: "8px 0",
                        borderTop: "1px solid var(--rc-gold-dim)" }}>
            Nothing in your diary today.
          </div>
        ) : (
          <div style={{ borderTop: "1px solid var(--rc-gold-dim)", paddingTop: 6 }}>
            <div style={{ fontSize: 9, letterSpacing: 1.5, opacity: 0.6, marginBottom: 4 }}>TODAY</div>
            {items.map((r, i) => {
              const t = (r.trigger_at || "").slice(11, 16);
              return (
                <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 10 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace",
                                color: "var(--rc-gold)", minWidth: 36 }}>{t || "—"}</div>
                  <div style={{ color: "var(--rc-text)", lineHeight: 1.3, flex: 1 }}>{r.text || ""}</div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }
  if (kind === "applaunch") {
    // Branded launch tile — Raw Company, SnapDog, Clothing, etc. Click to
    // open the full app in the in-app browser. Owner asked for these to be
    // included in the morning brief many times.
    const brandPalette = {
      rawcompany: { bg1: "#1a1208", bg2: "#3d2a0e", fg: "#ffd47a", glow: "rgba(255,184,0,0.8)" },
      snapdog:    { bg1: "#1a0e2e", bg2: "#3d1769", fg: "#d4a8ff", glow: "rgba(180,120,255,0.7)" },
      clothing:   { bg1: "#0e1a2e", bg2: "#0a3d69", fg: "#a8d4ff", glow: "rgba(120,180,255,0.7)" },
      rawmixes:   { bg1: "#2e1a0e", bg2: "#693d17", fg: "#ffd4a8", glow: "rgba(255,180,120,0.7)" },
      racing:     { bg1: "#0e2e1a", bg2: "#176939", fg: "#a8ffd4", glow: "rgba(120,255,180,0.7)" },
      trader:     { bg1: "#2e0e0e", bg2: "#691717", fg: "#ffa8a8", glow: "rgba(255,120,120,0.7)" },
      capital:    { bg1: "#0e2a1a", bg2: "#176939", fg: "#a8ffcc", glow: "rgba(120,255,180,0.8)" },
    };
    const brand = brandPalette[data.brandKey] || brandPalette.rawcompany;
    const open = () => {
      window.dispatchEvent(new CustomEvent("rawco-open-in-browser", {
        detail: { url: data.url, title: data.appName || "App" }
      }));
    };
    return (
      <div
        onClick={open}
        style={{
          margin: "-12px -12px 0",
          padding: "32px 16px",
          background: `linear-gradient(135deg, ${brand.bg1} 0%, ${brand.bg2} 100%)`,
          color: brand.fg,
          textAlign: "center",
          cursor: "pointer",
          borderRadius: 4,
        }}
      >
        <div style={{
          marginBottom: 10,
          display: "flex", justifyContent: "center",
          filter: `drop-shadow(0 0 18px ${brand.glow})`,
        }}>
          {/* Live logo from each app's apple-touch-icon (with graceful
              fallback through favicon → Google's favicon proxy). Owner
              asked: "I want my actual logos throughout — no more emojis". */}
          <AppLogo brandKey={data.brandKey} size={68} />
        </div>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: 14, letterSpacing: 3, opacity: 0.9,
        }}>{(data.appName || "App").toUpperCase()}</div>
        {/* Per-app live stats from the Raw Hub roll-up. Owner repeatedly
            asked: "tell me views, subscribers, revenue for each app". If
            no stats are wired up for that app yet we just hide the row
            so the launcher still looks clean. */}
        {data.stats && (() => {
          const s = data.stats;
          const bits = [];
          if (typeof s.revenue_gbp === "number") bits.push(`£${fmt(s.revenue_gbp)}`);
          if (typeof s.users === "number" && s.users > 0) bits.push(`${s.users} users`);
          if (typeof s.views === "number" && s.views > 0) bits.push(`${s.views} views`);
          if (typeof s.subscribers === "number" && s.subscribers > 0) bits.push(`${s.subscribers} subs`);
          if (!bits.length) return null;
          return (
            <div style={{
              marginTop: 8, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: 1.2, opacity: 0.85,
              color: brand.fg,
            }}>{bits.join(" · ")}</div>
          );
        })()}
        <div style={{
          marginTop: 12, padding: "4px 12px",
          border: `1px solid ${brand.fg}55`, borderRadius: 999,
          fontSize: 9, letterSpacing: 2, opacity: 0.75,
          display: "inline-block",
        }}>TAP TO OPEN</div>
      </div>
    );
  }
  if (kind === "stat") {
    return (
      <>
        <div className="proof-big-num">{data.value ?? "—"}</div>
        <div className="proof-sub">{data.label || ""}</div>
        {data.detail && <div className="mt-2 text-[11px] opacity-70">{data.detail}</div>}
      </>
    );
  }
  // Fallback: dump raw JSON
  return <pre className="text-[10px] opacity-70" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>;
}

function fmt(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
